import React, { useEffect, useMemo, useState } from 'react';
import { Card, Form, Radio, Button, Space, App, Alert, Tag, Typography } from 'antd';
import { SaveOutlined, ReloadOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { createAuthApi } from '@zero/auth-admin-ui';

const { Text } = Typography;

/**
 * SecurityConfigPage — admin chỉnh các cấu hình bảo mật toàn hệ thống.
 *
 * Currently:
 *   - Auth.TwoFactor.Mode: Disabled | Optional | Required
 *
 * Lưu trong Auth_AppConfig (IsPublic=0). Hiệu lực ngay sau save —
 * UserService.AuthenticateAsync đọc DB mỗi lần login (không cache).
 */
const TWO_FA_MODES = [
  {
    value: 'Disabled',
    label: 'Tắt toàn bộ',
    color: 'default',
    description: 'Hệ thống bỏ qua mọi 2FA — kể cả user có TwoFactorEnabled=1. Chỉ dùng khi sự cố lost secret database hoặc tạm khóa 2FA toàn bộ. KHÔNG khuyến nghị cho production.',
  },
  {
    value: 'Optional',
    label: 'Tùy chọn (mặc định)',
    color: 'blue',
    description: 'User và admin tự chọn bật 2FA per user. Per-user requirement có thể áp qua Role.Require2FA. Đây là hành vi NIST khuyến nghị cho doanh nghiệp tổng quát.',
  },
  {
    value: 'Required',
    label: 'Bắt buộc toàn hệ thống',
    color: 'red',
    description: 'Mọi user buộc setup 2FA. User chưa có 2FA login bị refuse với ErrorCode TWO_FACTOR_SETUP_REQUIRED — frontend redirect vào flow setup. Phù hợp môi trường compliance cao (PCI DSS, HIPAA, ISO 27001).',
  },
];

export function SecurityConfigPage({ axios }) {
  const api = useMemo(() => createAuthApi(axios), [axios]);
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [currentMode, setCurrentMode] = useState('Optional');

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.listAppConfig();
      const list = r.data?.Data ?? [];
      const mode = list.find((e) => e.ConfigKey === 'Auth.TwoFactor.Mode')?.ConfigValue ?? 'Optional';
      form.setFieldsValue({ TwoFactorMode: mode });
      setCurrentMode(mode);
    } catch (err) {
      message.error(err?.response?.data?.Message ?? err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const save = async (values) => {
    setSaving(true);
    try {
      await api.upsertAppConfig('Auth.TwoFactor.Mode', {
        ConfigKey: 'Auth.TwoFactor.Mode',
        ConfigValue: values.TwoFactorMode,
        ValueType: 'String',
        IsPublic: false,
      });
      setCurrentMode(values.TwoFactorMode);
      message.success(t('common.savedAt') + ' ' + new Date().toLocaleTimeString());
    } catch (err) {
      message.error(err?.response?.data?.Message ?? err.message);
    } finally {
      setSaving(false);
    }
  };

  const currentDef = TWO_FA_MODES.find((m) => m.value === currentMode);

  return (
    <Card size="small" extra={
      <Button size="small" icon={<ReloadOutlined />} onClick={refresh}>Reload</Button>
    }>
      <Alert
        type="info" showIcon style={{ marginBottom: 12 }}
        message={<Space><SafetyCertificateOutlined />Cấu hình bảo mật hệ thống</Space>}
        description={
          <span>
            Trạng thái hiện tại: <Tag color={currentDef?.color}>{currentDef?.label}</Tag>.
            Thay đổi áp dụng tức thì cho lần đăng nhập kế tiếp — không cần restart.
          </span>
        }
      />

      <Form form={form} layout="vertical" onFinish={save} disabled={loading} autoComplete="off">
        <Form.Item
          name="TwoFactorMode"
          label={<strong>Chế độ 2FA toàn hệ thống</strong>}
          rules={[{ required: true }]}
        >
          <Radio.Group style={{ width: '100%' }}>
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              {TWO_FA_MODES.map((m) => (
                <Radio key={m.value} value={m.value} style={{ width: '100%' }}>
                  <div style={{ display: 'inline-block', marginLeft: 4 }}>
                    <div>
                      <Tag color={m.color}>{m.label}</Tag>
                      <Text code style={{ fontSize: 11 }}>{m.value}</Text>
                    </div>
                    <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.55)', marginTop: 4, maxWidth: 720 }}>
                      {m.description}
                    </div>
                  </div>
                </Radio>
              ))}
            </Space>
          </Radio.Group>
        </Form.Item>

        <Form.Item style={{ marginBottom: 0 }}>
          <Button type="primary" htmlType="submit" loading={saving} icon={<SaveOutlined />}>
            Save security config
          </Button>
        </Form.Item>
      </Form>

      <Alert
        type="warning" showIcon style={{ marginTop: 16 }}
        message="Lưu ý"
        description={
          <ul style={{ paddingLeft: 18, margin: 0, fontSize: 13 }}>
            <li>Đổi sang <strong>Required</strong> khi đa số user đã có 2FA. User chưa setup sẽ bị refuse login đến khi hoàn tất /setup-2fa.</li>
            <li>Đổi sang <strong>Disabled</strong> chỉ khi cần khóa khẩn cấp (sự cố secret leak). Production không nên chạy lâu dài ở mode này.</li>
            <li>Per-user requirement vẫn có thể áp qua <Text code>Auth_Role.Require2FA = 1</Text> ở mode Optional.</li>
          </ul>
        }
      />
    </Card>
  );
}
